// Reel Preview Modal — client-side slideshow that approximates the rendered MP4
// using the same motion and entry transition options. Lets the owner sanity-check
// the order, captions, motion, and transitions BEFORE waiting for the backend
// ffmpeg render.

import { useEffect, useRef, useState } from "react";
import { X, Play, Pause, RotateCcw } from "lucide-react";

const FAMILY_CSS = {
    sans: "system-ui, -apple-system, sans-serif",
    condensed: "Saira Condensed, Oswald, Impact, sans-serif",
    serif: "Georgia, 'Times New Roman', serif",
    mono: "ui-monospace, 'SF Mono', Menlo, monospace",
};
// Caption sticker size. Either the per-photo `sizePx` slider value (exact pixel
// size at REEL_W=1080, used verbatim by the backend renderer) or the S/M/L
// preset, which is the same width-ratio the backend's FONT_SIZES are calibrated
// to (S=100/1080≈9%, M=152/1080≈14%, L=216/1080≈20%).
const PRESET_PX_AT_1080 = { S: 100, M: 152, L: 216 };
const POS_STYLE = {
    "top-left": { top: "5%", left: "5%", textAlign: "left" },
    "top-center": { top: "5%", left: "50%", transform: "translateX(-50%)", textAlign: "center" },
    "bottom-left": { bottom: "12%", left: "5%", textAlign: "left" },
    "bottom-center": { bottom: "12%", left: "50%", transform: "translateX(-50%)", textAlign: "center" },
};

const TRANSITION_DUR_MS = 450;

function motionTransform(motion, progress) {
    // progress 0 -> 1 over the photo's duration
    const p = Math.max(0, Math.min(1, progress));
    switch (motion) {
        case "zoom-in":
            return `scale(${1.04 + 0.16 * p})`;
        case "zoom-out":
            return `scale(${1.2 - 0.16 * p})`;
        case "pan-left":
            return `scale(1.18) translateX(${(0.5 - p) * 12}%)`;
        case "pan-right":
            return `scale(1.18) translateX(${(p - 0.5) * 12}%)`;
        case "pan-up":
            return `scale(1.18) translateY(${(0.5 - p) * 12}%)`;
        case "pan-down":
            return `scale(1.18) translateY(${(p - 0.5) * 12}%)`;
        case "static":
        default:
            return "scale(1.0)";
    }
}

function entryStyle(transition, age) {
    // age = ms since photo became active; entry runs for TRANSITION_DUR_MS
    const t = Math.max(0, Math.min(1, age / TRANSITION_DUR_MS));
    if (t >= 1) return { opacity: 1, transform: "translate3d(0,0,0)", clipPath: "none" };
    const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
    switch (transition) {
        case "fade":
        case "fade-black":
            return { opacity: ease };
        case "slide-left":
            return { opacity: 1, transform: `translateX(${(1 - ease) * 100}%)` };
        case "slide-right":
            return { opacity: 1, transform: `translateX(-${(1 - ease) * 100}%)` };
        case "slide-up":
            return { opacity: 1, transform: `translateY(${(1 - ease) * 100}%)` };
        case "slide-down":
            return { opacity: 1, transform: `translateY(-${(1 - ease) * 100}%)` };
        case "wipe-left":
            return { opacity: 1, clipPath: `inset(0 0 0 ${(1 - ease) * 100}%)` };
        case "wipe-right":
            return { opacity: 1, clipPath: `inset(0 ${(1 - ease) * 100}% 0 0)` };
        case "dissolve":
            return { opacity: ease };
        case "circle-open":
            return { opacity: 1, clipPath: `circle(${ease * 75}% at 50% 50%)` };
        case "pixelize":
            return { opacity: ease, filter: `blur(${(1 - ease) * 16}px)` };
        case "cut":
        default:
            return { opacity: 1 };
    }
}

export default function ReelPreviewModal({ files, perPhoto, onClose }) {
    const [activeIdx, setActiveIdx] = useState(0);
    const [elapsedMs, setElapsedMs] = useState(0); // since active photo started
    const [playing, setPlaying] = useState(true);
    const [stageWidth, setStageWidth] = useState(420);
    const rafRef = useRef();
    const startRef = useRef(performance.now());
    const stageRef = useRef(null);
    const totalMsPerPhoto = perPhoto * 1000;

    // Measure the stage width so caption + position math is identical to what
    // the backend renderer produces at 1080px wide.
    useEffect(() => {
        if (!stageRef.current) return undefined;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect?.width;
            if (w) setStageWidth(w);
        });
        ro.observe(stageRef.current);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!playing) return;
        const tick = (now) => {
            const dt = now - startRef.current;
            startRef.current = now;
            setElapsedMs((e) => {
                const next = e + dt;
                if (next >= totalMsPerPhoto) {
                    setActiveIdx((i) => {
                        if (i + 1 >= files.length) {
                            setPlaying(false);
                            return i;
                        }
                        return i + 1;
                    });
                    return 0;
                }
                return next;
            });
            rafRef.current = requestAnimationFrame(tick);
        };
        startRef.current = performance.now();
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [playing, activeIdx, files.length, totalMsPerPhoto]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === " ") {
                e.preventDefault();
                setPlaying((p) => !p);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const restart = () => {
        setActiveIdx(0);
        setElapsedMs(0);
        setPlaying(true);
        startRef.current = performance.now();
    };

    const active = files[activeIdx];
    if (!active) return null;

    const progress = Math.min(1, elapsedMs / totalMsPerPhoto);
    const transform = motionTransform(active.motion || "zoom-in", progress);
    const entry = activeIdx === 0 ? { opacity: 1 } : entryStyle(active.transition || "fade", elapsedMs);
    const capText = (active.caption?.text || "").trim();
    const capFont = FAMILY_CSS[active.caption?.family || "sans"];
    const sizeKey = active.caption?.size || "M";
    // Source-of-truth: explicit sizePx (slider) wins; otherwise preset px. Then
    // scale to the current stage width so it visually matches the exported MP4.
    const capPxAt1080 = active.caption?.sizePx || PRESET_PX_AT_1080[sizeKey] || PRESET_PX_AT_1080.M;
    const capSize = Math.max(10, Math.round(stageWidth * (capPxAt1080 / 1080)));
    const capPos = POS_STYLE[active.caption?.position || "top-left"];
    const capPadX = Math.max(6, Math.round(capSize * 0.25));
    const capPadY = Math.max(4, Math.round(capSize * 0.16));
    // Use the same fit the backend will use (auto-detected on upload).
    const fitObj = (active.fit || "cover") === "contain" ? "contain" : "cover";

    return (
        <div
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="reel-preview-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="relative w-full max-w-[420px] flex flex-col items-center">
                <div className="w-full flex items-center justify-between mb-3">
                    <div className="text-[11px] uppercase tracking-widest text-zinc-400">
                        Preview · #{activeIdx + 1}/{files.length}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="reel-preview-close"
                        className="text-zinc-400 hover:text-white inline-flex items-center gap-1 text-sm"
                    >
                        <X className="w-4 h-4" /> Close
                    </button>
                </div>

                <div
                    ref={stageRef}
                    className="relative bg-black overflow-hidden rounded-sm border border-white/10"
                    style={{ aspectRatio: "9 / 16", width: "100%" }}
                    data-testid="reel-preview-stage"
                >
                    {/* Blurred backdrop (only used in contain mode — gives the same
                        "Reel-style" no-black-bars look the renderer produces). */}
                    {fitObj === "contain" && (
                        <img
                            src={active.previewUrl}
                            alt=""
                            aria-hidden
                            className="absolute inset-0 w-full h-full"
                            style={{
                                objectFit: "cover",
                                filter: "blur(28px) brightness(0.7) saturate(1.2)",
                                transform: "scale(1.15)",
                                ...entry,
                            }}
                        />
                    )}
                    {/* Foreground image (the actual photo) */}
                    <img
                        src={active.previewUrl}
                        alt=""
                        className="absolute inset-0 w-full h-full"
                        style={{
                            objectFit: fitObj,
                            transform,
                            transformOrigin: "center center",
                            transition: "transform linear",
                            ...entry,
                        }}
                    />
                    {/* Caption overlay (meme-style sticker) */}
                    {capText && (
                        <div
                            className="absolute max-w-[88%] pointer-events-none select-none"
                            style={{
                                ...capPos,
                                fontFamily: capFont,
                                fontWeight: 900,
                                fontSize: `${capSize}px`,
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                color: "#fff",
                                background: "rgba(0,0,0,0.84)",
                                padding: `${capPadY}px ${capPadX}px`,
                                borderRadius: 4,
                                textTransform: "uppercase",
                                ...(activeIdx === 0 ? {} : entryStyle("fade", elapsedMs)),
                            }}
                            data-testid="reel-preview-caption"
                        >
                            {capText}
                        </div>
                    )}

                    {/* Per-photo progress bar */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
                        <div className="h-full bg-red-500" style={{ width: `${progress * 100}%`, transition: "width 80ms linear" }} />
                    </div>
                </div>

                {/* Photo thumbnails strip */}
                <div className="mt-3 w-full flex gap-1 overflow-x-auto">
                    {files.map((f, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => { setActiveIdx(i); setElapsedMs(0); setPlaying(true); startRef.current = performance.now(); }}
                            data-testid={`reel-preview-thumb-${i}`}
                            className={`shrink-0 w-10 aspect-[9/16] rounded-sm border ${i === activeIdx ? "border-red-500" : "border-white/10"} overflow-hidden`}
                        >
                            <img src={f.previewUrl} alt="" className="w-full h-full" style={{ objectFit: (f.fit || "cover") === "contain" ? "contain" : "cover" }} />
                        </button>
                    ))}
                </div>

                <div className="mt-3 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setPlaying((p) => !p)}
                        data-testid="reel-preview-play-toggle"
                        className="inline-flex items-center gap-1.5 text-xs text-white border border-white/20 hover:border-white px-3 py-1.5 rounded-sm"
                    >
                        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        {playing ? "Pause" : "Play"}
                    </button>
                    <button
                        type="button"
                        onClick={restart}
                        data-testid="reel-preview-restart"
                        className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white border border-white/20 px-3 py-1.5 rounded-sm"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Restart
                    </button>
                    <span className="text-[10px] text-zinc-500 ml-2">
                        Approximate — final MP4 may look slightly smoother.
                    </span>
                </div>
            </div>
        </div>
    );
}
